import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IsActiveMatchOptions, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { RouteLoadingService } from '@shared/service/route-loading.service';
import { CurrentUserQuery } from '@state/auth-state/current-user.query';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FuseUtilsService } from '../../../../../services/utils/utils.service';
import { FuseNavigationService } from '../../../navigation.service';
import { FuseNavigationItem } from '../../../navigation.types';
import { FuseVerticalNavigationComponent } from '../../vertical.component';
@Component({
  selector: 'fuse-vertical-navigation-basic-item',
  templateUrl: './basic.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgTemplateOutlet,
    RouterLink,
    RouterLinkActive,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslocoModule
  ]
})
export class FuseVerticalNavigationBasicItemComponent implements OnInit, OnDestroy {
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _fuseNavigationService = inject(FuseNavigationService);
  private _fuseUtilsService = inject(FuseUtilsService);
  private readonly currentUserQuery = inject(CurrentUserQuery);
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);
  readonly routeLoadingService = inject(RouteLoadingService);
  readonly item = input<FuseNavigationItem>(undefined);
  readonly name = input<string>(undefined);
  isActiveMatchOptions: IsActiveMatchOptions;
  private _fuseVerticalNavigationComponent: FuseVerticalNavigationComponent;
  private _unsubscribeAll: Subject<void> = new Subject<void>();
  accessType: any;
  organizationType: any;
  /**
   * Constructor
   */
  constructor() {
    // Set the equivalent of {exact: false} as default for active match options.
    // We are not assigning the item.isActiveMatchOptions directly to the
    // [routerLinkActiveOptions] because if it's "undefined" initially, the router
    // will throw an error and stop working.
    this.isActiveMatchOptions = this._fuseUtilsService.subsetMatchOptions;
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  /**
   * On init
   */
  ngOnInit(): void {
    this.getCurrentUserData();
    // Set the "isActiveMatchOptions" either from item's
    // "isActiveMatchOptions" or the equivalent form of
    // item's "exactMatch" option
    const item = this.item();
    this.isActiveMatchOptions =
      (item.isActiveMatchOptions ?? item.exactMatch)
        ? this._fuseUtilsService.exactMatchOptions
        : this._fuseUtilsService.subsetMatchOptions;
    // Get the parent navigation component
    this._fuseVerticalNavigationComponent = this._fuseNavigationService.getComponent(this.name());
    // Mark for check
    this._changeDetectorRef.markForCheck();
    // Subscribe to onRefreshed on the navigation component
    this._fuseVerticalNavigationComponent.onRefreshed
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // Mark for check
        this._changeDetectorRef.markForCheck();
      });
  }
  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
  private getCurrentUserData(): void {
    this.currentUserQuery
      .select()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((state: any) => {
        // Logout resets the store to { data: {} }; skip the empty-user emission.
        if (!state?.data?.userType) {
          return;
        }
        this.organizationType = state.data?.userType?.toLowerCase();
        if (
          this.organizationType === 'factory' ||
          this.organizationType === 'brand' ||
          state.data.viewAs === 'from_admin_to_factory' ||
          state.data.viewAs === 'from_admin_to_brand'
        ) {
          this.translocoService.setActiveLang(state.data.language);
        }
        this._changeDetectorRef.markForCheck();
      });
  }

  onItemClick(event?: MouseEvent): void {
    // A modified click (cmd/ctrl/shift/alt) or a non-primary button opens the
    // link in a new tab/window without navigating the current one, so no
    // NavigationEnd fires to clear the loader — skip it to avoid a stuck spinner.
    if (
      event &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
    ) {
      return;
    }
    const link = this.item()?.link;
    if (!link || this.item()?.disabled) {
      return;
    }
    const currentUrl = this.router.url.split('?')[0].split('#')[0].replace(/\/+$/, '');
    const targetUrl = link.split('?')[0].split('#')[0].replace(/\/+$/, '');
    if (currentUrl !== targetUrl) {
      this.routeLoadingService.setPendingTarget(link);
      this._changeDetectorRef.markForCheck();
    }
  }

  isItemLoading(): boolean {
    return this.routeLoadingService.isRoutePending(this.item()?.link);
  }
}
